import { useState } from "react";
import { Check, ChevronDown, ChevronUp, Zap, Send, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import type { ActionProposal } from "./types";

interface ActionCardProps {
  action: ActionProposal;
  onValidate: (id: string) => void;
  onPriorityChange: (id: string, priority: ActionProposal["priority"]) => void;
}

const priorityColors: Record<string, string> = {
  haute: "bg-destructive/15 text-destructive border-destructive/30",
  moyenne: "bg-primary/15 text-primary border-primary/30",
  basse: "bg-muted text-muted-foreground border-border",
};

const priorityLabels: Record<string, string> = {
  haute: "🔴 Haute",
  moyenne: "🟡 Moyenne",
  basse: "🟢 Basse",
};

export const ActionCard = ({ action, onValidate, onPriorityChange }: ActionCardProps) => {
  const [showPriority, setShowPriority] = useState(false);

  const executeAction = () => {
    const lower = `${action.title} ${action.description}`.toLowerCase();
    // Detect email action: open draft email
    const emailMatch = action.description.match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
    if (emailMatch || lower.includes("email") || lower.includes("e-mail") || lower.includes("mail")) {
      const to = emailMatch ? emailMatch[0] : "";
      const subject = encodeURIComponent(action.title);
      const body = encodeURIComponent(action.description);
      window.open(`mailto:${to}?subject=${subject}&body=${body}`);
      toast.success("Brouillon email ouvert");
      onValidate(action.id);
      return;
    }
    // Detect quote/invoice action: navigate to CRM
    if (lower.includes("devis") || lower.includes("facture") || lower.includes("contrat")) {
      window.location.href = "/crm";
      return;
    }
    // Detect call action
    const phoneMatch = action.description.match(/(\+?\d[\d\s.-]{7,})/);
    if (phoneMatch || lower.includes("appel") || lower.includes("téléphone")) {
      if (phoneMatch) {
        window.open(`tel:${phoneMatch[0].replace(/\s/g, "")}`);
      } else {
        window.location.href = "/appels";
      }
      onValidate(action.id);
      return;
    }
    // Detect meeting/agenda action
    if (lower.includes("rendez-vous") || lower.includes("réunion") || lower.includes("rdv") || lower.includes("agenda")) {
      window.location.href = "/agenda";
      return;
    }
    // Detect nanocorp chain
    if (lower.includes("chaîne") || lower.includes("nanocorp") || lower.includes("agent")) {
      window.location.href = "/nanocorp";
      return;
    }
    // Default: mark as validated
    onValidate(action.id);
    toast.success("Action validée");
  };

  return (
    <div
      className={`rounded-xl border p-3 transition-all duration-200 ${
        action.validated
          ? "bg-primary/5 border-primary/30"
          : "bg-card border-border/60 hover:border-primary/20"
      }`}
      data-testid="action-card"
    >
      <div className="flex items-start gap-2">
        <div className="mt-0.5">
          <Zap className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-tight">{action.title}</p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{action.description}</p>

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {/* Priority selector */}
            <div className="relative">
              <button
                onClick={() => setShowPriority(!showPriority)}
                className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${priorityColors[action.priority]}`}
              >
                {priorityLabels[action.priority]}
                {showPriority ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
              {showPriority && (
                <div className="absolute top-full left-0 mt-1 z-10 bg-popover border border-border rounded-lg shadow-lg p-1 min-w-[120px]">
                  {(["haute", "moyenne", "basse"] as const).map((p) => (
                    <button
                      key={p}
                      onClick={() => {
                        onPriorityChange(action.id, p);
                        setShowPriority(false);
                      }}
                      className="w-full text-left text-xs px-2 py-1.5 rounded-md hover:bg-accent transition-colors"
                    >
                      {priorityLabels[p]}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Execute button */}
            <Button
              size="sm"
              className="h-6 text-[11px] px-2 rounded-full gap-1"
              onClick={executeAction}
              data-testid={`execute-action-${action.id}`}
            >
              <Send className="h-3 w-3" />
              Exécuter
            </Button>

            {/* Validate button */}
            <Button
              size="sm"
              variant={action.validated ? "default" : "outline"}
              className="h-6 text-[11px] px-2 rounded-full"
              onClick={() => onValidate(action.id)}
            >
              <Check className="h-3 w-3 mr-1" />
              {action.validated ? "Validé ✓" : "Valider"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
